// Stilart for ét GIS-lag (en "owner" i LERLayer): ensfarvet eller farvet efter en attribut, linjebredde, stiplet og punktstørrelse.
// Ren logik uden THREE, så den kan prøves for sig: dialogen i PointView bruger listAttributes/buildLegend til at vise valgmuligheder,
// og LERLayer bruger makeColorResolver til at give hvert objekt sin farve.
//
// style = {
//   mode: "default" | "single" | "attribute",   default = lagets egne farver efter forsyningsart (som før)
//   color: "#rrggbb",                           mode single
//   attribute: "status",                        mode attribute: se listAttributes
//   colors: {"<klasse>": "#rrggbb"},            brugerens egne farver pr. klasse (resten får en automatisk farve)
//   lineWidth: 1..8,                            pixels (1 = tynd linje)
//   dashed: false,
//   pointSize: 1                                faktor på punkt- og komponentstørrelse (0.5..4)
// }

export const MAX_CLASSES = 24; // flere forskellige værdier end dette grupperes (tal) eller samles i en klasse "øvrige" (tekst)
const NUMERIC_CLASS_COUNT = 5;

export const DEFAULT_STYLE = Object.freeze({
	mode: "default", color: "#3355d8", attribute: null, colors: {}, lineWidth: 1, dashed: false, pointSize: 1,
});

// Letlæselige, adskilte farver (efter ColorBrewer Paired/Set1) - klasserne får dem i rækkefølge
export const CLASS_PALETTE = [
	"#1f78b4", "#e31a1c", "#33a02c", "#ff7f00", "#6a3d9a", "#b15928", "#a6cee3", "#fb9a99", "#b2df8a", "#fdbf6f", "#cab2d6", "#ffff99",
];
// Rampe til tal i klasser (lys -> mørk)
const RAMP = ["#fee5d9", "#fcae91", "#fb6a4a", "#de2d26", "#a50f15"];
export const OTHER_COLOR = "#999999";
// Navnene vises aldrig (dialogen oversætter efter klassens id); de er kun nøgler til brugerens egne farver
export const OTHER_LABEL = "~other";
export const EMPTY_LABEL = "~none";

// Felter, der altid kan vælges, når de findes på objekterne (resten kommer fra attrs og danva.raw)
const BASE_FIELDS = ["supply", "type", "subtype", "status", "danger", "owner", "established", "diameter_m"];

export function normalizeStyle(style){
	const s = Object.assign({}, DEFAULT_STYLE, style || {});
	s.colors = Object.assign({}, s.colors || {});
	s.lineWidth = clamp(Number(s.lineWidth) || 1, 1, 8);
	s.pointSize = clamp(Number(s.pointSize) || 1, 0.5, 4);
	s.dashed = !!s.dashed;
	if(s.mode !== "single" && s.mode !== "attribute") s.mode = "default";
	if(s.mode === "attribute" && !s.attribute) s.mode = "default";
	return s;
}

export function isDefaultStyle(style){
	const s = normalizeStyle(style);
	return s.mode === "default" && s.lineWidth === 1 && !s.dashed && s.pointSize === 1;
}

export function hexToNumber(hex){
	const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ""));
	return m ? parseInt(m[1], 16) : 0x999999;
}

export function numberToHex(n){
	return "#" + (n >>> 0).toString(16).padStart(6, "0").slice(-6);
}

function clamp(v, lo, hi){
	return Math.max(lo, Math.min(hi, v));
}

// Værdien af en attribut på et objekt. "attrs.X" og "danva.X" peger ind i de rå data; alt andet er et felt direkte på objektet.
export function attributeValue(feature, key){
	if(!feature || !key) return null;
	let v;
	if(key.startsWith("attrs.")) v = feature.attrs ? feature.attrs[key.slice(6)] : undefined;
	else if(key.startsWith("danva.")) v = feature.danva && feature.danva.raw ? feature.danva.raw[key.slice(6)] : undefined;
	else v = feature[key];
	if(v === undefined || v === null) return null;
	if(typeof v === "object") return null;
	const text = String(v).trim();
	return text === "" ? null : v;
}

const isNumericValue = (v) => typeof v === "number" ? Number.isFinite(v) : (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v.replace(",", "."))));
const toNumber = (v) => typeof v === "number" ? v : Number(String(v).replace(",", "."));

// De attributter, der kan farves efter, med antal forskellige værdier: [{key, label, distinct, numeric}], de mest brugbare først
// (få forskellige værdier = god til klasser). Felter uden nogen værdi, og felter hvor alle har forskellig værdi (id'er), udelades.
export function listAttributes(features){
	const keys = new Map(); // key -> {label}
	for(const k of BASE_FIELDS) keys.set(k, {label: k});
	for(const f of features){
		if(f.attrs) for(const k of Object.keys(f.attrs)) if(!keys.has("attrs." + k)) keys.set("attrs." + k, {label: k});
		if(f.danva && f.danva.raw) for(const k of Object.keys(f.danva.raw)) if(!k.startsWith("@") && !keys.has("danva." + k)) keys.set("danva." + k, {label: k});
	}
	const out = [];
	for(const [key, meta] of keys){
		const values = new Map();
		let numeric = true;
		let any = false;
		for(const f of features){
			const v = attributeValue(f, key);
			if(v === null) continue;
			any = true;
			values.set(String(v), (values.get(String(v)) || 0) + 1);
			if(numeric && !isNumericValue(v)) numeric = false;
		}
		if(!any) continue;
		const distinct = values.size;
		if(distinct < 2) continue;
		if(!numeric && distinct > MAX_CLASSES) continue; // tekst med et utal af værdier (id'er, navne) giver ingen brugbare klasser
		out.push({key, label: meta.label, distinct, numeric});
	}
	return out.sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
}

function rank(attr){
	const classy = attr.distinct <= 12 ? 0 : attr.distinct <= MAX_CLASSES ? 1 : 2;
	return classy * 2 + (BASE_FIELDS.includes(attr.key) ? 0 : 1);
}

// Inddeler en attribut i klasser: [{id, label, count}]. Tal med mange værdier bliver 5 lige store intervaller, ellers én klasse pr. værdi
// (de mest almindelige først, højst MAX_CLASSES, resten samles i "Øvrige"). Objekter uden værdi får klassen "(ingen værdi)".
export function classify(features, attribute){
	const values = features.map(f => attributeValue(f, attribute));
	const present = values.filter(v => v !== null);
	const distinct = new Set(present.map(String));
	const numeric = present.length > 0 && present.every(isNumericValue);
	let classOf;
	let order = [];

	if(numeric && distinct.size > 12){
		const nums = present.map(toNumber);
		const min = Math.min(...nums), max = Math.max(...nums);
		const step = (max - min) / NUMERIC_CLASS_COUNT || 1;
		const fmt = (n) => Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
		const bins = [];
		for(let i = 0; i < NUMERIC_CLASS_COUNT; i++){
			const lo = min + step * i, hi = i === NUMERIC_CLASS_COUNT - 1 ? max : min + step * (i + 1);
			bins.push({id: `bin${i}`, label: `${fmt(lo)} – ${fmt(hi)}`});
		}
		order = bins;
		classOf = (v) => v === null ? "none" : `bin${Math.min(NUMERIC_CLASS_COUNT - 1, Math.floor((toNumber(v) - min) / step))}`;
	}else{
		const counts = new Map();
		for(const v of present) counts.set(String(v), (counts.get(String(v)) || 0) + 1);
		let sorted = [...counts.entries()];
		if(numeric) sorted.sort((a, b) => toNumber(a[0]) - toNumber(b[0]));
		else sorted.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
		const keep = sorted.slice(0, MAX_CLASSES).map(e => e[0]);
		const keepSet = new Set(keep);
		order = keep.map(k => ({id: "v:" + k, label: k}));
		if(sorted.length > MAX_CLASSES) order.push({id: "other", label: OTHER_LABEL});
		classOf = (v) => v === null ? "none" : keepSet.has(String(v)) ? "v:" + String(v) : "other";
	}

	const tally = new Map();
	const ids = values.map(v => { const id = classOf(v); tally.set(id, (tally.get(id) || 0) + 1); return id; });
	if(tally.has("none")) order.push({id: "none", label: EMPTY_LABEL});
	const classes = order.filter(c => tally.has(c.id)).map(c => ({id: c.id, label: c.label, count: tally.get(c.id)}));
	return {classes, classIds: ids, numericRamp: numeric && distinct.size > 12};
}

// Farve pr. klasse: [{id, label, count, color}]. style.colors vinder (nøglen er klassens tekst), resten får paletten i rækkefølge.
export function buildLegend(features, style){
	const s = normalizeStyle(style);
	if(s.mode !== "attribute") return [];
	const {classes, numericRamp} = classify(features, s.attribute);
	let next = 0;
	return classes.map((c, i) => {
		let auto;
		if(c.id === "none" || c.id === "other") auto = OTHER_COLOR;
		else if(numericRamp) auto = RAMP[i % RAMP.length];
		else auto = CLASS_PALETTE[next++ % CLASS_PALETTE.length];
		return {id: c.id, label: c.label, count: c.count, color: s.colors[c.label] || auto};
	});
}

// Giver hvert objekt dets farve (et tal 0xRRGGBB). fallback(feature) bruges for "default" (lagets egne farver).
export function makeColorResolver(features, style, fallback){
	const s = normalizeStyle(style);
	if(s.mode === "single"){
		const n = hexToNumber(s.color);
		return () => n;
	}
	if(s.mode === "attribute"){
		const {classIds} = classify(features, s.attribute);
		const legend = buildLegend(features, s);
		const byId = new Map(legend.map(l => [l.id, hexToNumber(l.color)]));
		const index = new Map(features.map((f, i) => [f, i]));
		return (feature) => {
			const i = index.get(feature);
			return i === undefined ? hexToNumber(OTHER_COLOR) : (byId.get(classIds[i]) ?? hexToNumber(OTHER_COLOR));
		};
	}
	return fallback;
}
